import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTheme } from "../../context/ThemeProvider";
import { parseNebulaResponse } from "../../utils/nebulaParser";
import { getDisplayAnswerId } from "../../store/promptStore";

const NebulaMessageDots = ({ messages = [], activeIndex, selectedAnswerByPrompt = {}, onSelectIndex, onRegenerateFromIndex, viewMode = "canvas", onToggleOverview }) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [contextMenuIndex, setContextMenuIndex] = useState(null);
  const contextMenuRef = useRef(null);
  const activeDotRef = useRef(null);

  // Dot timeline row scrolls horizontally (overflow-x-auto) instead of
  // wrapping once dots exceed the available width, and its scrollbar is
  // hidden — so without this, switching to a dot near either edge (or a
  // new message landing there) could leave the active dot scrolled out of
  // view with no way to tell where it is. Keep it in view whenever it changes.
  useEffect(() => {
    activeDotRef.current?.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
  }, [activeIndex]);

  // Close context menu on outside click
  useEffect(() => {
    if (contextMenuIndex === null) return;
    const handler = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenuIndex(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [contextMenuIndex]);
  const activeMessage = messages[activeIndex];
  const activeAnswerId = selectedAnswerByPrompt[activeMessage?.id] || getDisplayAnswerId(activeMessage?.answers);
  const activeAnswer = activeMessage?.answers?.find((answer) => answer.id === activeAnswerId);

  // Collapse whenever user switches to a different dot
  useEffect(() => {
    setIsExpanded(false);
  }, [activeIndex]);

  const previewWords = activeAnswer?.content
    ? activeAnswer.content
        .replace(/```[\s\S]*?```/g, "")                      // strip code fences
        .replace(/!\[.*?\]\(.*?\)/g, "")                     // strip image markdown (Firebase paths etc.)
        .replace(/\s+/g, " ")                                // collapse leftover whitespace
        .trim()
        .split(/\s+/)
        .slice(0, 10)
        .join(" ")
    : null;
  const photonPreview = previewWords?.length > 0 ? previewWords + "…" : null;

  return (
    <div className="flex flex-col border-b border-edge-border dark:border-edge-canvas-hover">
      {/* Dot timeline row */}
      <div className="flex items-center gap-2 px-6 py-3 overflow-x-auto scrollbar-hide">
        {/* Nebula / Atlas segmented toggle */}
        {messages.length > 0 && (
          <div className="shrink-0 flex items-center rounded-md border border-edge-hex-b0bec8 dark:border-edge-muted-ink overflow-hidden text-[11px] font-medium">
            <button
              onClick={() => viewMode === "canvas" && onToggleOverview?.()}
              className={`px-2.5 py-0.5 transition-colors ${
                viewMode === "overview"
                  ? "bg-fill-primary text-ink-on-primary"
                  : "text-ink-toolbar-muted-text dark:text-ink-muted-control hover:text-ink-ink dark:hover:text-ink-white"
              }`}
            >
              cluster
            </button>
            <div className="w-px h-3.5 bg-fill-hex-b0bec8 dark:bg-fill-muted-ink" />
            <button
              onClick={() => viewMode === "overview" && onToggleOverview?.()}
              className={`px-2.5 py-0.5 transition-colors ${
                viewMode === "canvas"
                  ? "bg-fill-primary text-ink-on-primary"
                  : "text-ink-toolbar-muted-text dark:text-ink-muted-control hover:text-ink-ink dark:hover:text-ink-white"
              }`}
            >
              nova
            </button>
          </div>
        )}

        <div className="relative flex items-center gap-3 flex-1 min-w-0">
          {messages.map((message, index) => {
            const answerId = selectedAnswerByPrompt[message.id] || getDisplayAnswerId(message.answers);
            const answer = message.answers?.find((candidate) => candidate.id === answerId);
            const artifact = answer ? parseNebulaResponse(answer.content) : null;
            const isActive = index === activeIndex;
            const hasArtifact = artifact && artifact.type !== "text";

            return (
              <React.Fragment key={message.id || index}>
                {index > 0 && (
                  <div className={`h-px w-4 shrink-0 ${theme === "dark" ? "bg-fill-canvas-hover" : "bg-fill-border"}`} />
                )}
                <div className="relative shrink-0">
                  <motion.button
                    ref={isActive ? activeDotRef : undefined}
                    onClick={() => onSelectIndex(index)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenuIndex(index);
                    }}
                    title={message.content?.slice(0, 80) || "Message"}
                    whileHover={{ scale: 1.3 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    className={`shrink-0 rounded-full transition-colors ${
                      hasArtifact
                        ? `w-3 h-3 ${isActive ? "bg-fill-ink dark:bg-fill-white ring-2 ring-offset-1 ring-focus-ink dark:ring-focus-white dark:ring-offset-focus-toolbar-contrast-surface" : "bg-fill-canvas-accent"}`
                        : `w-2.5 h-2.5 ${isActive ? "bg-fill-ink dark:bg-fill-white" : "bg-fill-toolbar-border dark:bg-fill-toolbar-neutral-hover"}`
                    }`}
                  />
                  {/* Context menu */}
                  {contextMenuIndex === index && (
                    <div
                      ref={contextMenuRef}
                      className="absolute top-5 left-0 z-50 min-w-[160px] bg-fill-white dark:bg-fill-hex-1e2a38 border border-edge-gray-200 dark:border-edge-canvas-hover rounded-lg shadow-lg py-1 text-[12px]"
                    >
                      <button
                        className="w-full text-left px-3 py-1.5 text-ink-ink dark:text-ink-white hover:bg-fill-gray-50 dark:hover:bg-fill-toolbar-neutral-surface transition-colors"
                        onClick={() => {
                          setContextMenuIndex(null);
                          onRegenerateFromIndex?.(index);
                        }}
                      >
                        Regenerate from here
                      </button>
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Preview area — collapsed by default, expandable */}
      {activeMessage && (
        <div className="px-6 pb-2">
          {/* Always visible: truncated You + Photon preview + toggle arrow */}
          <div className="flex items-start gap-2 text-[12px]">
            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold text-ink-ink dark:text-ink-white shrink-0">You:</span>
                <span className="text-ink-hex-3d4f5c dark:text-ink-hex-a8b8c3 truncate">
                  {activeMessage.content?.slice(0, 80) + (activeMessage.content?.length > 80 ? "…" : "")}
                </span>
              </div>
              {photonPreview && (
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-ink-canvas-accent shrink-0">Photon:</span>
                  <span className="text-ink-control-border dark:text-ink-hex-8896a5 truncate">{photonPreview}</span>
                </div>
              )}
            </div>

            {/* Expand / collapse toggle */}
            <button
              onClick={() => setIsExpanded(v => !v)}
              className="shrink-0 ml-1 mt-0.5 flex items-center justify-center w-5 h-4 rounded-full bg-fill-gray-100 dark:bg-fill-panel-surface hover:bg-fill-gray-200 dark:hover:bg-fill-toolbar-hover text-ink-gray-500 dark:text-ink-gray-400 transition-colors shadow-sm"
            >
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>

          {/* Expanded: full You message, scrollable with cap */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="mt-2 max-h-36 overflow-y-auto text-[12px] text-ink-hex-3d4f5c dark:text-ink-hex-a8b8c3 leading-relaxed pr-2">
                  {activeMessage.content}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default NebulaMessageDots;

